import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, TextField } from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { appColor } from "../../theme/appColor";
import Logo from "../../Images/logo_main_black.webp";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { useAppDispatch } from "../../Store/hooks";
import { useNotifier } from "../../Core/Notifier";
import { useLoginMutation } from "../../Api/attoDeskApi";
import { useFormik } from "formik";
import { IAuth } from "../../Api/Interface/api.interface";
import { setEnableAuth } from "../../Store/Auth/AuthSlice";

const Login: React.FC = () => {
  // const navigate = useNavigate();
  // const authContext = useContext(AuthContext);

  // if (!authContext) {
  //   return null;
  // }

  // const { login } = authContext;

  // const handleLogin = () => {
  //   login();
  //   navigate("/dashboard");
  // };
  const dispatch = useAppDispatch();
  const { showErrorMessage } = useNotifier();
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Toggles password visibility on the password field. Default off so
  // a shoulder-surfer never sees the plaintext before the operator
  // explicitly asks for it.
  const [showPw, setShowPw] = useState(false);

  // Post-reset confirmation banner — survives the page reload that
  // clears Redux state because it's keyed off a URL query parameter.
  // The query parameter is cleared on first render so it doesn't linger
  // and re-show on subsequent visits.
  const [resetSuccess, setResetSuccess] = useState(false);
  useEffect(() => {
    if (searchParams.get("reset") === "success") {
      setResetSuccess(true);
      const next = new URLSearchParams(searchParams);
      next.delete("reset");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  //Form validation
  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    onSubmit: async (values) => {
      try {
        const loginResponse = await login(values).unwrap();
        if (!loginResponse.status) {
          showErrorMessage(loginResponse.message);
        } else {
          const tokenData = loginResponse.data as IAuth;
          localStorage.setItem("token", tokenData.token);
          dispatch(setEnableAuth());
          navigate('/dashboard/list');
        } 
      } catch (error) {
        showErrorMessage("Please check you username or password");
      }

      
    },
  });
  const formValid = useMemo(() => {
    return formik.values.username === "" || formik.values.password === ""
      ? false
      : true;
  }, [formik]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box
        sx={{
          backgroundColor: appColor.white,
          boxShadow: 3,
          // Tighter padding on phones so the card doesn't waste horizontal
          // space; full bleed within the parent's max-width on every size.
          p: { xs: 3, sm: 4, md: 5 },
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 4,
          }}
        >
          <img
            src={Logo}
            alt="AttoDesk Login"
            style={{ width: "100%", height: "auto",maxWidth:"100px", display: "block" }}
          />
        </Box>

        {resetSuccess && (
          <Alert severity="success" sx={{ mb: 1.5, "& .MuiAlert-message": { fontSize: 13 } }}>
            Password reset successful. Please log in with your new password.
          </Alert>
        )}

        <TextField
          fullWidth
          variant="filled"
          label="Username"
          margin="normal"
          sx={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #CCCCCC",
          }}
          {...formik.getFieldProps('username')}
        />
        <TextField
          fullWidth
          variant="filled"
          label="Password"
          type={showPw ? "text" : "password"}
          margin="normal"
          sx={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #CCCCCC",
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPw((p) => !p)}
                  // Mouse-down on the toggle would steal focus from the
                  // input; preventing default keeps the cursor in place.
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  sx={{ color: "#475569", mr: 0.5 }}
                >
                  {showPw
                    ? <VisibilityOffRoundedIcon fontSize="small" />
                    : <VisibilityRoundedIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          {...formik.getFieldProps('password')}
        />

        {/* Forgot Password link — placed under the password field, before
            the Login button, per spec. Subtle styling so it stays out of
            the way of the primary CTA but is always reachable. */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5, mb: 1 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            underline="hover"
            sx={{ fontSize: 12.5, color: "#475569", fontWeight: 600,
              "&:hover": { color: "#0d1b2a" } }}
          >
            Forgot password?
          </Link>
        </Box>

        <Button
          variant="contained"
          disabled={!formValid || isLoading}
          sx={{
            backgroundColor: appColor.black,
            color: "white",
            mt: 2,
            py: 1.1,
            borderRadius: 0,
            // Full-width on mobile so the CTA can't end up tiny in a
            // corner. Auto-sized on desktop where the layout has room.
            width: { xs: "100%", sm: "auto" },
            minWidth: { sm: 140 },
            fontWeight: 700,
            letterSpacing: 0.5,
            "&:hover": {
              backgroundColor: appColor.black,
            },
            "&:active": {
              backgroundColor: appColor.black,
            },
          }}
          type="submit"
          // onClick={handleLogin}
        >
          {isLoading && (
            <Box sx={{ pr: 2, pt: 0.5 }}>
              <CircularProgress size={16} sx={{ color: "white" }} />
            </Box>
          )}
          Login
        </Button>
      </Box>
    </form>
  );
};

export default React.memo(Login);
